import { DateTime } from 'luxon';
import React from 'react';

import Time from 'src/enosikit/components/Time';

/**
 * Formats a time range for standalone display with semantic HTML using React Time components.
 * This function is similar to buildTimeComponentSummary but wraps the formatted dates in
 * Time components for proper semantic HTML and accessibility.
 *
 * Use this function when you need to display time ranges with proper HTML time elements
 * that provide additional semantic meaning and machine-readable timestamps.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {object} active - The active start and finish timestamps.
 * @param {number} [active.start] - The active start timestamp in seconds since epoch.
 * @param {number} [active.finish] - The active finish timestamp in seconds since epoch.
 * @returns {React.ReactNode} - A formatted time range message with Time components.
 * @example
 * // Returns React elements with Time components for start and finish dates
 * buildSummary(intl, { start: 1672498800, finish: 1704034799 });
 * // In code: React elements with Time components
 * // In browser: "Active from 1 Jan 2023 until 31 Dec 2023" (with proper semantic HTML)
 *
 * // Returns React elements with Time component for start date only
 * buildSummary(intl, { start: 1672498800, finish: null });
 * // In code: React elements with Time component
 * // In browser: "Active from 1 Jan 2023" (with proper semantic HTML)
 *
 * // Returns React elements with Time component for finish date only
 * buildSummary(intl, { start: null, finish: 1704034799 });
 * // In code: React elements with Time component
 * // In browser: "Active until 31 Dec 2023" (with proper semantic HTML)
 *
 * // Returns plain text when no dates are provided
 * buildSummary(intl, { start: null, finish: null });
 * // In browser: "Active always"
 * @see buildTimeRangeSummary - Returns plain strings instead of React elements
 * @see buildBadgeActiveSummary - For formatting time ranges for BadgeActive components
 */
const buildSummary = (intl, active) => {
  const { start, finish } = active || {};
  const startOnly = start && !finish;
  const finishOnly = !start && finish;
  const startAndFinish = start && finish;

  if (startOnly) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.start_only.text', defaultMessage: 'Active from {startDate}' }, { startDate });
  }
  if (finishOnly) {
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );
    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.finish_only.text', defaultMessage: 'Active until {finishDate}' }, { finishDate });
  }
  if (startAndFinish) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );

    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.start_and_finish.text', defaultMessage: 'Active from {startDate} until {finishDate}' }, { startDate, finishDate });
  }
  return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.active_always.text', defaultMessage: 'Active always' });
};

export default buildSummary;
